import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
// oxlint-disable-next-line no-restricted-imports -- kept to not break too many things in the config portal
import { Link } from 'react-router'

import { useConfigurationContext } from '../../context/configurationContext'
import { useTemplateContext } from '../../context/templateContext'
import { useTemplateIsLibrary } from '../../hooks/useTemplateIsLibrary'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import {
  getTemplateOperationAtDate,
  templateIdFromPath,
} from '../../uiModels/templateOperations/helpers'
import {
  type InheritableField,
  isInherited,
} from '../../uiModels/templateOperations/types'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { DocumentationEntry } from '../documentationEntry/DocumentationEntry'

type Props = {
  value: InheritableField
}

export const InheritedValueDocumentationEntry = ({ value }: Props) => {
  const { templateVersion } = useTemplateContext()
  const { server, branch } = useNavigationParams()
  const templateIsLibrary = useTemplateIsLibrary()
  const translate = useTranslate()
  const { workspaceKey } = useOperationsContext()
  const { viewAt } = useConfigurationContext()
  const { applicationTemplates: applicationTemplateOperations } =
    useApplicationViewOperationsContext()

  assertNotEmpty(templateVersion)

  const isFieldInherited = isInherited(value)
  const originTemplateId = value.origin
    ? templateIdFromPath(value.origin)
    : null

  // The inherited value should either be on the same layer as the current value or on a layer below.
  const originTemplate = originTemplateId
    ? getTemplateOperationAtDate(
        applicationTemplateOperations.get(originTemplateId),
        viewAt,
      )
    : undefined

  const isFieldFromReplacedParent =
    isFieldInherited &&
    templateVersion.versionInfo.parent &&
    originTemplateId !== templateVersion.versionInfo.parent?.identifier &&
    !templateVersion?.versionInfo.parent.replacedByIds?.includes(
      templateVersion.versionInfo.identifier,
    )

  return isFieldInherited && originTemplateId ? (
    <DocumentationEntry
      title={translate('templateViewer.fieldDetails.field.inheritance')}
      value={
        <>
          {isFieldFromReplacedParent && (
            <>
              <Link
                to={configurationItem({
                  workspaceName: workspaceKey?.name,
                  layer: templateVersion.versionInfo.parent!.layer,
                  block: templateVersion.versionInfo.parent!.block,
                  server,
                  itemId: templateVersion.versionInfo.parent!.identifier,
                  branch,
                  type: templateIsLibrary(
                    templateVersion.versionInfo.parent!.identifier,
                  )
                    ? 'library'
                    : 'template',
                })}
                className="rounded-md bg-inheritance p-2 text-amaze-inkBright hover:opacity-90"
              >
                {templateVersion.versionInfo.parent!.identifier}
              </Link>
              <span className="mx-2">
                {translate('templateViewer.fieldDetails.field.replacedBy')}
              </span>
            </>
          )}
          {originTemplate && (
            <Link
              to={configurationItem({
                workspaceName: workspaceKey?.name,
                layer: originTemplate.layer,
                block: originTemplate.block,
                server,
                itemId: originTemplateId,
                branch,
                type: templateIsLibrary(originTemplateId)
                  ? 'library'
                  : 'template',
              })}
              className="rounded-md bg-inheritance p-2 text-amaze-inkBright hover:opacity-90"
            >
              {originTemplateId}
            </Link>
          )}
        </>
      }
    />
  ) : null
}
